// src/components/aiTalentDrive/tracks.ts
// AI Talent Drive tracks and job descriptions, shared by the candidate page
// (HiringCircuit.tsx) and the company sign-up page (AiTalentDriveCompanies.tsx).

// Tracks on the page. The code before " · " is what the registration API
// receives (see trackCode), so the codes of live tracks must not change.
export const TRACKS = [
  "FDE–01 · Forward Deployed Engineer",
  "GAI–02 · AI Engineer",
  "FS–03 · Full Stack Engineer",
  "BE–04 · Backend Engineer",
  "FE–05 · Frontend Engineer",
  "PM–06 · AI Product Manager",
  "GTM–07 · GTM Engineer",
  "INT–08 · Software / AI Engineering Intern",
  "DE–09 · Data Engineer",
];

// Experience bands per track, in the order they are offered. Each band has
// its own job description. Both lists must match TRACK_EXPERIENCE /
// EXPERIENCE_OPTIONS in the backend (companyapp/hiring_circuit.py).
export const ANY_EXPERIENCE = "Open for all years of experience";
export const STUDENT = "Student / recent graduate";
export const TRACK_BANDS: Record<string, string[]> = {
  "FDE–01 · Forward Deployed Engineer": [ANY_EXPERIENCE],
  "GAI–02 · AI Engineer": ["0–2 years", "2–5 years"],
  "FS–03 · Full Stack Engineer": ["0–2 years", "2–5 years"],
  "BE–04 · Backend Engineer": ["0–2 years", "2–5 years"],
  "FE–05 · Frontend Engineer": ["0–2 years", "2–5 years"],
  "PM–06 · AI Product Manager": [ANY_EXPERIENCE],
  "GTM–07 · GTM Engineer": [ANY_EXPERIENCE],
  "INT–08 · Software / AI Engineering Intern": [STUDENT],
  "DE–09 · Data Engineer": ["0–2 years", "2–5 years"],
};
export const bandsFor = (track: string) => TRACK_BANDS[track] || [];
// Band label -> code the registration API expects
export const EXPERIENCE_CODES: Record<string, string> = {
  "0–2 years": "0-2",
  "2–5 years": "2-5",
  [ANY_EXPERIENCE]: "any",
  [STUDENT]: "student",
};

// Tracks are shown in two groups, short-term work first
export const isShortTerm = (track: string) => track.startsWith("INT–");
export const TRACK_GROUPS = [
  { label: "Part-time / short-duration projects", tracks: TRACKS.filter(isShortTerm) },
  { label: "Full-time roles", tracks: TRACKS.filter((t) => !isShortTerm(t)) },
];
// A track keeps the same accent colour whichever group it is listed in
export const accentFor = (track: string) =>
  TRACK_ACCENTS[TRACKS.indexOf(track) % TRACK_ACCENTS.length];

// One accent per track pill, sampled left to right along the page's
// blue → cyan → amber gradient so the row reads as that gradient.
export const TRACK_ACCENTS = [
  "#60A5FA", "#22D3EE", "#86D9A0", "#FBBF24", "#F472B6", "#C4B5FD", "#A3E635", "#FB923C", "#2DD4BF",
];

// Job descriptions, keyed by track label, then by experience band. Content
// comes from the AI Jobs Drive Role Skills Framework; tools are examples,
// not requirements.
export type Tool = { label: string; text: string };
export type JobDescription = {
  skills: string[];
  qualifications: string[];
  tools: Tool[];
};
export type BandedJD = Partial<Record<string, JobDescription>>;
const tool = (label: string, text: string): Tool => ({ label, text });

export const JOB_DESCRIPTIONS: Partial<Record<string, BandedJD>> = {
  "FDE–01 · Forward Deployed Engineer": {
    [ANY_EXPERIENCE]: {
      skills: [
        "Running customer discovery and turning messy business workflows into technical scope",
        "Building LLM-powered solutions inside customer environments as prototypes",
        "Delivering agents, MCP servers and integrations with customer data, auth and legacy systems",
        "Using evals to measure adoption and workflow impact, and feeding field insights back to Product and Research",
        "Communicating clearly with customers and executives, and making scope/speed/quality trade-offs",
        "Working with the production engineering team for final deployments",
      ],
      qualifications: [
        "B.Tech/M.Tech in CS, Maths, Physics, Data Science or related engineering field",
        "Open to all years of experience: software engineering plus customer-facing work as FDE, solutions architect, implementation or consulting engineer",
        "Shipped at least one LLM-powered system to real users; former founders or early startup engineers valued",
        "Travel up to 25–50%",
        "Strong written/spoken English and client-presentation skills",
      ],
      tools: [
        tool("", "Claude Code / Codex or other vibe coding tools"),
        tool("", "Web app and database deployment skills"),
      ],
    },
  },
  "GAI–02 · AI Engineer": {
    "0–2 years": {
      skills: [
        "Integrating LLM APIs with prompt design, structured outputs and function calling",
        "Building basic RAG applications end to end (ingest, embed, retrieve, generate)",
        "Writing clean, tested Python and consuming or exposing REST APIs",
        "Evaluating LLM output critically and iterating on prompts using simple eval sets",
        "Learning fast and using AI coding assistants productively and responsibly",
      ],
      qualifications: [
        "B.Tech/M.Tech in CS, AI or related field, or equivalent practical experience (final-year students for intern-to-hire)",
        "At least one deployed GenAI project (RAG chatbot, agent, voice/doc assistant) with a public GitHub repo or live demo",
        "Internship at a startup or product company, or hackathon wins involving LLMs",
        "DSA and CS fundamentals",
        "Short courses (DeepLearning.AI, Hugging Face, AWS/Azure AI fundamentals) are a plus, not a substitute for projects",
      ],
      tools: [
        tool("LLM APIs and models", "OpenAI, Anthropic, Gemini or Hugging Face models, with prompt design and structured outputs"),
        tool("Frameworks and retrieval", "LangChain or LlamaIndex (or plain code) with a vector store such as Chroma, FAISS, pgvector or Pinecone"),
        tool("Evals and tracing", "simple eval sets or Ragas; basic tracing with Langfuse or LangSmith"),
        tool("Build and ship", "Python, Git, FastAPI/Flask, Docker, one cloud or a Streamlit/Gradio demo; AI coding assistants (Cursor, Claude Code, Copilot)"),
      ],
    },
    "2–5 years": {
      skills: [
        "Designing and productizing RAG pipelines (chunking, embeddings, hybrid search, reranking)",
        "Building agentic systems with tool/function calling, memory, orchestration and human-in-the-loop",
        "Building and operating MCP servers and integrations with enterprise APIs and data sources",
        "Setting up eval, tracing and prompt-versioning workflows to measure and improve quality",
        "Optimising cost, latency and reliability of live LLM traffic (streaming, caching, model routing, fallbacks)",
      ],
      qualifications: [
        "B.Tech/M.Tech in CS, AI or related field, or equivalent practical experience",
        "2–5 years of software engineering, with 1+ year shipping LLM features to real users",
        "Strong backend background (Python/FastAPI or Node.js) with production on-call ownership",
        "Track record of mentoring juniors and leading design reviews",
        "Open-source contributions, published evals or technical blogs on LLM systems are a plus",
      ],
      tools: [
        tool("LLM APIs and models", "calling a hosted model (OpenAI, Anthropic, Gemini) or running an open one (Hugging Face, Ollama), with prompting and structured outputs"),
        tool("Orchestration and agents", "chaining calls, tool use and agent loops (LangChain/LangGraph, LlamaIndex, MCP, or plain code)"),
        tool("Retrieval and data", "embeddings plus a vector store (pgvector, Chroma, FAISS, Pinecone) to build RAG over real documents"),
        tool("Evals, observability and shipping", "testing and tracing outputs (Ragas, Langfuse, LangSmith) and deploying behind an API (FastAPI, Docker, one cloud)"),
      ],
    },
  },
  "FS–03 · Full Stack Engineer": {
    "0–2 years": {
      skills: [
        "Have been part of a team that has shipped significant products and capabilities",
        "Building responsive UIs and connecting them to REST APIs and databases",
        "Implementing CRUD services with authentication, validation and error handling",
        "Writing readable, version-controlled code and taking part in code reviews",
        "Debugging across browser, server and database layers",
        "Shipping quickly with AI coding tools while understanding and verifying the output",
      ],
      qualifications: [
        "B.Tech/M.Tech in CS or related field, or equivalent proven experience",
        "1–2 deployed full-stack projects (live URL + GitHub), ideally one with an LLM feature",
        "Internship at a startup or product company, or strong freelance/open-source work",
        "Good DSA/problem-solving fundamentals",
        "Hackathon participation or GSoC-style contributions are a plus",
      ],
      tools: [
        tool("Frontend", "HTML/CSS/JavaScript, React or Next.js, Tailwind"),
        tool("Backend and data", "Node.js/Express or Python/FastAPI with PostgreSQL/MySQL or MongoDB"),
        tool("Deploy and collaborate", "Git/GitHub, Vercel/Netlify/Render, basic Docker"),
        tool("AI tools", "an LLM API used in a project; Cursor, Claude Code or Copilot used with verification"),
      ],
    },
    "2–5 years": {
      skills: [
        "Owned features end to end from discovery and data model to UI, API and production release",
        "Designed scalable APIs and schemas, and made documented architecture trade-offs",
        "Integrated LLM features (streaming chat, agents, RAG) into customer-facing products",
        "Ensured reliability through testing, CI/CD, observability and production debugging",
        "Reviewed code, mentored SDE-1s and worked closely with product and design",
      ],
      qualifications: [
        "B.Tech/M.Tech in CS or related field, or equivalent proven experience",
        "2–5 years building production AI-first, SaaS or consumer web apps",
        "Demonstrated ownership of at least one system or service in production",
        "Hands-on work with LLM APIs in shipped features is a strong plus",
      ],
      tools: [
        tool("Frontend", "React/Next.js with TypeScript; Tailwind or a component library"),
        tool("Backend and data", "Node.js, Python or Java APIs over PostgreSQL, MongoDB or Redis"),
        tool("AI features", "OpenAI/Anthropic/Gemini APIs, Vercel AI SDK or LangChain for streaming chat, agents and RAG"),
        tool("Shipping and reliability", "Docker, AWS/GCP/Vercel, GitHub Actions; Jest/Playwright tests and Sentry monitoring"),
      ],
    },
  },
  "BE–04 · Backend Engineer": {
    "0–2 years": {
      skills: [
        "Building RESTful APIs with authentication, validation and error handling",
        "Writing SQL and designing basic relational schemas",
        "Applying DSA and OOP fundamentals to write efficient, testable code",
        "Debugging, logging and writing unit/integration tests",
        "Learning distributed-systems basics (caching, queues, concurrency)",
      ],
      qualifications: [
        "B.Tech/B.E./MCA in CS/IT or related field",
        "Backend internship or 1–2 deployed API projects with documentation",
        "DSA and CS fundamentals (DBMS, OS, networks)",
        "Competitive programming or open-source contributions are a plus",
        "Exposure to a cloud platform or Docker through projects",
      ],
      tools: [
        tool("One primary language", "Node.js (Express), Python (FastAPI/Django/Flask), Java (Spring Boot) or Go"),
        tool("Databases", "PostgreSQL/MySQL, MongoDB, Redis basics"),
        tool("Build, test and deploy", "Git/GitHub, Postman, pytest/Jest/JUnit, Docker, Linux, AWS/GCP free tier"),
        tool("AI integration", "calling an LLM API from a backend service"),
      ],
    },
    "2–5 years": {
      skills: [
        "Designing and owning scalable services and APIs with clear contracts and SLAs",
        "Data modelling, query optimisation and caching across SQL and NoSQL stores",
        "Building event-driven systems with correct ordering, retries and failure handling",
        "Operating services in production through CI/CD, observability, incident response and security",
        "Leading architecture decisions, reviewing code and mentoring SDE-1s",
      ],
      qualifications: [
        "B.Tech/B.E./MCA in CS/IT or related field",
        "2–5 years of backend development at product/service companies or startups with real traffic",
        "Owned at least one service end to end, including on-call and scaling",
        "System-design skills (typically assessed in an HLD/LLD round)",
        "Experience with backends for AI features (LLM gateways, agent infra, vector search) is a strong plus",
      ],
      tools: [
        tool("Languages and APIs", "Node.js/TypeScript (NestJS, Express), Python (FastAPI, Django), Java (Spring Boot) or Go; REST, GraphQL, gRPC"),
        tool("Data and messaging", "PostgreSQL/MySQL, MongoDB, Redis, Elasticsearch, pgvector; Kafka, RabbitMQ, SQS, BullMQ/Celery"),
        tool("Infra and delivery", "AWS or GCP, Docker, Kubernetes, Terraform; GitHub Actions or Jenkins"),
        tool("Observability and security", "Datadog, Prometheus/Grafana, OpenTelemetry, Sentry; OAuth2/JWT"),
      ],
    },
  },
  "FE–05 · Frontend Engineer": {
    "0–2 years": {
      skills: [
        "Building responsive, accessible UI components from designs",
        "Integrating REST APIs and managing client-side state",
        "Writing clean, reusable HTML/CSS/JS/TypeScript",
        "Debugging UI and performance issues with browser dev tools",
        "Collaborating through Git workflows and taking review feedback well",
      ],
      qualifications: [
        "B.Tech/B.E./BCA/MCA in CS/IT or related field; strong self-taught portfolios considered",
        "2+ deployed frontend projects with live links and clean GitHub repos",
        "Frontend internship at a startup or product company",
        "Understanding of responsive design and accessibility basics",
        "Figma familiarity or design sense is a plus",
      ],
      tools: [
        tool("Core web", "HTML, CSS, JavaScript, TypeScript basics"),
        tool("Frameworks and state", "React or Next.js, Tailwind CSS; Redux, Zustand or Context API"),
        tool("Dev tools and deploy", "Git/GitHub, Chrome DevTools, Figma, Vercel/Netlify"),
        tool("AI coding tools", "Cursor, Copilot or Claude Code, with the candidate able to explain and verify the output"),
      ],
    },
    "2–5 years": {
      skills: [
        "Architecting scalable component systems and design systems in React/Next.js",
        "Optimising performance (Core Web Vitals, bundle size, rendering strategy: SSR/SSG/ISR)",
        "Building AI-native interfaces: streaming responses, chat and agent UIs, optimistic updates",
        "Ensuring accessibility, cross-browser quality and strong test coverage",
        "Mentoring juniors and turning Figma designs and product requirements into polished UX",
      ],
      qualifications: [
        "B.Tech/M.Tech in CS or related field, or equivalent portfolio",
        "2–5 years of production frontend experience on high-traffic or B2B SaaS products",
        "Demonstrated ownership of a frontend architecture decision or design-system rollout",
        "Strong product and UX sense; experience working closely with designers",
        "Public portfolio, open-source components or technical writing are a plus",
      ],
      tools: [
        tool("Frameworks and styling", "React/Next.js with TypeScript; Tailwind, shadcn/ui, Radix or CSS Modules; Vite or Webpack"),
        tool("State and data", "Zustand, Redux Toolkit, TanStack Query, React Hook Form + Zod"),
        tool("Quality and performance", "Jest/Vitest, React Testing Library, Playwright/Cypress, Storybook; Lighthouse and Sentry"),
        tool("AI and real-time UI", "Vercel AI SDK, WebSockets/SSE for streaming chat and agent interfaces"),
      ],
    },
  },
  "PM–06 · AI Product Manager": {
    [ANY_EXPERIENCE]: {
      skills: [
        "Owning an AI feature end to end: discovery, PRDs, user stories and acceptance criteria, prompt/model decisions, launch and iteration",
        "Prompt engineering and rapid prototyping to validate ideas before engineering time is spent",
        "Defining evals, golden datasets and quality metrics for LLM/agent features (not just engagement metrics), and designing for failure modes: guardrails, fallbacks, human-in-the-loop and incident response",
        "Understanding LLM fundamentals (how models fail, hallucination, RAG vs fine-tuning) and making cost/latency/quality trade-offs with engineering on model choice, context and architecture",
        "Data literacy and experimentation: writing SQL, instrumenting funnels, defining success metrics and running experiments; communicating crisply with engineers, designers and customers",
      ],
      qualifications: [
        "B.Tech/B.E. in CS or engineering, or MBA with a technical undergrad; technical fluency is expected at AI-first startups; final-year candidates considered for APM",
        "Open to all years of experience in product, engineering or data (a PM internship at a startup counts); candidates with 2+ years should have at least 1 year shipping LLM-powered features to real users",
        "Has personally built and shipped an agent, RAG app, prompt-driven prototype or workflow automation (GitHub or live demo)",
        "Prior engineering, data or ML background is a strong plus; experience with B2B SaaS or India-first consumer products, or startup/founder experience, is valued",
        "Hackathons, PM case competitions, product teardowns, APM programmes or AI PM coursework (Product School, Reforge, DeepLearning.AI) are a plus, not a substitute for building",
      ],
      tools: [
        tool("LLM platforms and prompting", "ChatGPT, Claude or Gemini as daily tools; OpenAI, Anthropic and Gemini APIs, open models via Hugging Face; playgrounds, prompt versioning and structured outputs"),
        tool("Evals and observability", "Langfuse, LangSmith, Braintrust, Ragas; LLM-as-judge and human review workflows"),
        tool("Prototyping", "Cursor, Claude Code, Lovable, Bolt, Emergent, Replit; n8n/Zapier/Make for workflow prototypes"),
        tool("Analytics and PM stack", "SQL, Google Sheets, Mixpanel, Amplitude, PostHog, Metabase, A/B testing; Linear, Jira, Notion, Figma/FigJam, Miro, Productboard"),
        tool("Concepts to know, whatever the tools", "RAG, agents, tool calling, MCP, fine-tuning basics, context windows, token economics"),
      ],
    },
  },
  "GTM–07 · GTM Engineer": {
    [ANY_EXPERIENCE]: {
      skills: [
        "Owned the lead generation for a product, across 1–2 channels",
        "Designing end-to-end outbound and inbound pipelines: sourcing, waterfall enrichment, ICP scoring, routing, sequencing",
        "Writing AI prompts and agents that return structured, verifiable output for research and personalisation",
        "Integrating CRM, enrichment and sequencing tools via APIs, webhooks and scripts",
        "Monitoring buying signals (job changes, funding, web visits, product usage) and triggering timely plays",
        "Running experiments, tracking pipeline metrics and maintaining data quality and email deliverability",
      ],
      qualifications: [
        "Degree in engineering or business",
        "Background in SDR/RevOps/growth or software engineering",
        "Open to all years of experience in RevOps, growth, marketing ops or a technical GTM role",
        "Understanding of B2B SaaS sales motions (PLG, outbound, ABM)",
        "Working SQL and basic Python/JavaScript; Clay certification or experience with Clay is useful",
      ],
      tools: [
        tool("Enrichment and signals", "Clay (Claygent, AI columns, waterfalls), Apollo, ZoomInfo, Clearbit, RB2B; Common Room, 6sense, Gong, UserGems"),
        tool("CRM and sequencing", "HubSpot, Salesforce; Outreach, Salesloft, Instantly, Smartlead, lemlist, HeyReach"),
        tool("Automation and AI", "n8n, Zapier, Make, Gumloop; OpenAI/Anthropic APIs, Claude Code; Lovable/Bolt for internal tools"),
        tool("Data", "SQL, Python/JavaScript, REST APIs/webhooks, Google Sheets, Looker"),
      ],
    },
  },
  "INT–08 · Software / AI Engineering Intern": {
    [STUDENT]: {
      skills: [
        "Writing working code in at least one language and picking up new frameworks quickly",
        "Vibe coded an app or extension end to end",
        "Integrating LLM APIs and building simple RAG/agent prototypes",
        "Debugging, testing and documenting work clearly",
        "Communicating progress, asking good questions and taking ownership of assigned tasks",
      ],
      qualifications: [
        "Pursuing (pre-final/final year) or recently completed B.Tech/B.E./BCA/MCA/M.Tech in CS, IT, AI/DS or related field",
        "1–3 personal, hackathon or coursework projects on GitHub, ideally one deployed with an AI feature",
        "Basic DSA and programming fundamentals",
        "Prior internship, open-source contributions or hackathon wins are a plus, not required",
      ],
      tools: [
        tool("Programming", "Python and/or JavaScript; React/Next.js or Node.js/FastAPI"),
        tool("Data", "any database; NumPy/Pandas/scikit-learn for AI-track interns"),
        tool("LLM building", "knowledge of OpenAI/Gemini/Claude APIs"),
        tool("Workflow", "Git/GitHub basics, Docker basics; Cursor, Copilot or Claude Code"),
      ],
    },
  },
  "DE–09 · Data Engineer": {
    "0–2 years": {
      skills: [
        "Strong fundamentals in Python and SQL for data processing and querying",
        "Understanding of ETL/ELT concepts and data pipeline development",
        "Familiarity with databases, data modelling and data warehousing",
        "Basic understanding of LLMs, embeddings, vector databases and RAG pipelines",
        "Problem-solving skills and ability to work with large datasets",
      ],
      qualifications: [
        "B.Tech/M.Tech in Computer Science, IT or a related field",
        "0–2 years of experience in data engineering, software engineering or related roles",
        "Hands-on projects or internship experience involving data pipelines or AI applications",
        "Familiarity with PySpark, Airflow, Spark or vector databases",
        "Exposure to AWS, Azure, GCP or LLM APIs is a plus",
      ],
      tools: [
        tool("Languages and data", "Python, SQL, PostgreSQL, PySpark"),
        tool("Orchestration and cloud", "Airflow; AWS, Azure or GCP"),
        tool("AI and vector search", "OpenAI APIs, LangChain, FAISS, Pinecone"),
      ],
    },
    "2–5 years": {
      skills: [
        "Strong proficiency in Python, PySpark and SQL for building scalable data pipelines",
        "Experience designing and maintaining ETL/ELT workflows and data orchestration",
        "Hands-on experience with cloud data platforms and data warehouses",
        "Understanding of LLM data pipelines, embeddings, vector databases and RAG architectures",
        "Experience with data quality, pipeline optimisation and processing large datasets",
      ],
      qualifications: [
        "B.Tech/M.Tech in Computer Science, IT or a related field",
        "2–5 years of hands-on experience in data engineering",
        "Experience building and maintaining production-grade data pipelines",
        "Exposure to AI/ML data workflows, LLM integrations or vector search systems",
        "Familiarity with Spark, Databricks, Kafka, Airflow, Pinecone, Weaviate or pgvector is a plus",
      ],
      tools: [
        tool("Languages and processing", "Python, PySpark, SQL, Spark, Databricks"),
        tool("Orchestration and cloud", "Airflow; AWS, Azure or GCP"),
        tool("AI and vector search", "OpenAI APIs, LangChain, Pinecone, Weaviate, pgvector"),
      ],
    },
  },
};

export const splitTrack = (label: string) => {
  const [code, name] = label.split(" · ");
  return { code, name };
};
